import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createGlobalState } from 'react-use';
import { useApiClient } from '../../../../../hooks/useRestAPIHooks';
import { useActiveProjectId } from '../../../../../hooks';
import { AnnotationData } from '../../../../TextAnnotation/types';

// ─── Types ───────────────────────────────────────────────────────────────────

type SuggestTaskStatus =
  | 'scheduled'
  | 'pending'
  | 'waiting'
  | 'processing'
  | 'failed'
  | 'completed'
  | 'expired';

type SuggestTask = {
  id: string;
  status: SuggestTaskStatus;
};

type SuggestAgentInvokeRequest = {
  clinical_impression_id: string;
  subject_id: string;
  project_id: string;
  instructions?: string;
};

// Global map of impressionId -> task so state survives impression remounts.
type TasksMap = Record<string, SuggestTask | undefined>;
const useGlobalTasks = createGlobalState<TasksMap>({});

const isTaskResolved = (status: SuggestTaskStatus) =>
  ['failed', 'completed', 'expired'].includes(status);

// ─── Annotation formatting ────────────────────────────────────────────────────

/**
 * Resolves the 1-indexed line range for `selectedText` within
 * `summaryMarkdown`. Returns `null` when the text cannot be found.
 * Line numbers match the numbered summary the agent sees (produced by
 * `_add_line_numbers` in `suggest_agent.py`).
 */
function getLineNumbers(
  selectedText: string,
  summaryMarkdown: string,
): { start: number; end: number } | null {
  // Resolves to the first occurrence; duplicate substrings get the same line
  // ref. The blockquote still matches, so the agent prompt stays valid.
  const idx = summaryMarkdown.indexOf(selectedText);
  if (idx === -1) return null;
  const before = summaryMarkdown.substring(0, idx);
  const startLine = (before.match(/\n/g) ?? []).length + 1;
  const endLine = startLine + (selectedText.match(/\n/g) ?? []).length;
  return { start: startLine, end: endLine };
}

/**
 * Builds the `instructions` string sent to the suggest agent.
 *
 * Structure:
 *   1. The user's free-text instructions (verbatim, if any).
 *   2. A numbered list of annotations, each with a blockquoted excerpt and the
 *      clinician's comment.
 */
export function buildSuggestInstructions(
  freeText: string | undefined,
  annotations: AnnotationData[],
  summaryMarkdown: string,
): string {
  const parts: string[] = [];

  if (freeText?.trim()) {
    parts.push(freeText.trim());
  }

  if (annotations.length > 0) {
    const items = annotations.map((ann, i) => {
      const lineNums = getLineNumbers(ann.selectedText, summaryMarkdown);

      let lineRef: string;
      if (!lineNums) {
        lineRef = '';
      } else if (lineNums.end > lineNums.start) {
        lineRef = ` on lines ${lineNums.start}-${lineNums.end}`;
      } else {
        lineRef = ` on line ${lineNums.start}`;
      }

      const quoted = ann.selectedText
        .split('\n')
        .map(l => `> ${l}`)
        .join('\n');

      return `${i + 1}. For the excerpt${lineRef}:\n\n${quoted}\n\n${
        ann.comment
      }`;
    });

    parts.push(
      'The clinician has annotated the following sections:\n\n' +
        items.join('\n\n'),
    );
  }

  return parts.join('\n\n');
}

// ─── Hook ────────────────────────────────────────────────────────────────────

type UseSuggestAgentOptions = {
  impressionId: string;
  patientId: string;
  onSuccess?: () => void;
  onFailure?: () => void;
  onConflict?: () => void;
  onSuggestingChange?: (isSuggesting: boolean) => void;
};

/**
 * Manages a suggest-agent invocation for a single impression. Task state is
 * stored globally via `createGlobalState` so it persists when the component
 * that hosts this hook remounts (e.g. when the user navigates between
 * impressions and then back).
 */
export function useSuggestAgent({
  impressionId,
  patientId,
  onSuccess,
  onFailure,
  onConflict,
  onSuggestingChange,
}: UseSuggestAgentOptions) {
  const apiClient = useApiClient();
  const projectId = useActiveProjectId();

  const [tasksOrUndef, setTasks] = useGlobalTasks();
  const tasks: TasksMap = useMemo(() => tasksOrUndef ?? {}, [tasksOrUndef]);
  const task = tasks[impressionId];

  const tasksRef = useRef<TasksMap>(tasks);
  tasksRef.current = tasks;

  const setTask = useCallback(
    (next: SuggestTask | undefined) => {
      setTasks({ ...tasksRef.current, [impressionId]: next });
    },
    [setTasks, impressionId],
  );

  const onSuccessRef = useRef(onSuccess);
  const onFailureRef = useRef(onFailure);
  const onConflictRef = useRef(onConflict);
  const onSuggestingChangeRef = useRef(onSuggestingChange);
  onSuccessRef.current = onSuccess;
  onFailureRef.current = onFailure;
  onConflictRef.current = onConflict;
  onSuggestingChangeRef.current = onSuggestingChange;

  // Poll the active task for this impression.
  useEffect(() => {
    if (!task?.id || isTaskResolved(task.status)) return;

    let cancelled = false;
    let running = false;

    const checkForUpdates = async () => {
      if (running) return;
      running = true;
      try {
        const response = (await apiClient.request(
          'GET /v1/agents-v2/suggest/invocations/:id' as any,
          { id: task.id },
        )) as { data: SuggestTask | undefined };

        if (cancelled) return;

        const invocation = response.data;

        if (!invocation || isTaskResolved(invocation.status)) {
          setTask(undefined);
          if (invocation?.status === 'completed') {
            onSuccessRef.current?.();
          } else if (
            invocation?.status === 'failed' ||
            invocation?.status === 'expired'
          ) {
            onFailureRef.current?.();
          }
        } else {
          setTask(invocation);
        }
      } catch {
        // ignore transient polling errors
      } finally {
        running = false;
      }
    };

    const intervalId = setInterval(checkForUpdates, 1000);
    checkForUpdates();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, task?.id]);

  const isSuggesting =
    !!task &&
    ['scheduled', 'pending', 'waiting', 'processing'].includes(task.status);

  const hasMountedSuggestingRef = useRef(false);
  useEffect(() => {
    if (!hasMountedSuggestingRef.current) {
      hasMountedSuggestingRef.current = true;
      if (!isSuggesting) return;
    }
    onSuggestingChangeRef.current?.(isSuggesting);
  }, [isSuggesting]);

  const invokeSuggestion = useCallback(
    async (
      freeText: string | undefined,
      annotations: AnnotationData[],
      summaryMarkdown: string,
    ) => {
      const instructions = buildSuggestInstructions(
        freeText,
        annotations,
        summaryMarkdown,
      );

      const body: SuggestAgentInvokeRequest = {
        clinical_impression_id: impressionId,
        subject_id: patientId,
        project_id: projectId,
        ...(instructions ? { instructions } : {}),
      };

      try {
        const res = (await apiClient.request(
          'POST /v1/agents-v2/suggest/invoke' as any,
          body,
        )) as {
          headers?: Record<string, string>;
          data?: { task_id?: string };
        };

        const taskId =
          (res as any)?.headers?.location?.split('/').pop() ??
          res.data?.task_id;

        if (taskId) {
          setTask({ id: taskId, status: 'pending' });
        }
      } catch (err: any) {
        if (err?.response?.status === 409) {
          onConflictRef.current?.();
          const location: string | undefined = err?.response?.headers?.location;
          const existingTaskId = location?.split('/').pop();
          if (existingTaskId) {
            setTask({ id: existingTaskId, status: 'pending' });
          }
          return;
        }
        throw err;
      }
    },
    [apiClient, impressionId, patientId, projectId, setTask],
  );

  return { invokeSuggestion, isSuggesting };
}
